import { Icon } from '@zoom/components';
import classnames from 'classnames';
import { TFunction } from 'i18next';
import { intersection } from 'ramda';
import React, { Dispatch, useMemo } from 'react';

import {
  DropdownItem,
  IUncontrolledDropdown,
  UncontrolledDropdown,
} from '../../../components/Dropdown/UncontrolledDropdown';
import { SETTINGS_CLONE_ALL_SCHEDULE, SETTINGS_DROPDOWN_TOGGLE, SETTINGS_TOGGLE_AGENTS } from '../constants';
import { ChartScheduleElement, DaySettings } from '../types';
import { PinUnpinSubmenus } from './PinUnpinSubmenus';
import {
  Action,
  pinAllScheduleElements,
  pinElementsInCurrentDay,
  pinSelectedElements,
  unpinAllScheduleElements,
  unpinElementsInCurrentDay,
  unpinSelectedElements,
  updateDaySetting,
} from './actions';

interface SettingsDropdownProps {
  dispatch: Dispatch<Action>;
  ganttScheduleElements: ChartScheduleElement[];
  isView: boolean;
  pickedDayIndex: number;
  pinnedScheduleElementsIds: string[];
  pinnedUsedScheduleElementsIds: string[];
  selectedScheduleElementsIds: string[];
  setCloneMenuOpened?: (state: boolean) => void;
  settings: DaySettings;
  showActiveIndicator?: boolean;
  t: TFunction;
  totalPinnableElementIds: string[];
  usedScheduleElements: ChartScheduleElement[]; // TODO: could be removed
}

export const SettingsDropdown: React.FC<SettingsDropdownProps> = ({
  dispatch,
  ganttScheduleElements,
  isView,
  pickedDayIndex,
  pinnedScheduleElementsIds,
  pinnedUsedScheduleElementsIds,
  selectedScheduleElementsIds,
  setCloneMenuOpened,
  settings,
  showActiveIndicator = false,
  t,
  totalPinnableElementIds,
}) => {
  const pinnedSelectedScheduleElementsIds = useMemo(
    () =>
      ganttScheduleElements
        .filter(({ key, selected }) => pinnedScheduleElementsIds.includes(key) && selected)
        .map(({ key }) => key),
    [ganttScheduleElements, pinnedScheduleElementsIds]
  );

  const pinnableSelectedScheduleElementsIds = useMemo(
    () => intersection(selectedScheduleElementsIds, totalPinnableElementIds),
    [selectedScheduleElementsIds, totalPinnableElementIds]
  );

  const pinnableGanttScheduleElementsIds = useMemo(
    () =>
      intersection(
        totalPinnableElementIds,
        ganttScheduleElements.map(({ key }) => key)
      ),
    [ganttScheduleElements, totalPinnableElementIds]
  );

  const dropdownProps: IUncontrolledDropdown = {
    className: classnames('dropdown-menu-basic dropdown-menu-basic--right-side-arrow', {
      'dropdown-menu-basic--active': showActiveIndicator,
    }),
    toggle: {
      dataTestId: SETTINGS_DROPDOWN_TOGGLE,
      children: <Icon kind="menu-kebab" color="#00a8e2" size={20} />,
      props: {
        color: 'info',
        title: t('layout.settings'),
      },
    },
    menu: {
      props: {
        modifiers: {
          offset: {
            enabled: true,
            offset: '0, 8',
          },
          preventOverflow: {
            // To be able set negative x position
            enabled: true,
            boundariesElement: 'window',
          },
        },
      },
      children: (
        <>
          <DropdownItem
            data-test-id={SETTINGS_TOGGLE_AGENTS}
            onClick={() => {
              dispatch(updateDaySetting(pickedDayIndex, 'hideNonWorkingAgents', !settings.hideNonWorkingAgents));
            }}
          >
            {settings.hideNonWorkingAgents ? (
              <>
                <Icon kind="non-workers-on" height={16} color="blue" />
                {t('person.showAllPeople')}
              </>
            ) : (
              <>
                <Icon kind="non-workers-off" height={16} color="red" />
                {t('person.hideNonWorkingPeople')}
              </>
            )}
          </DropdownItem>
          {!isView && (
            <>
              <DropdownItem
                data-test-id={SETTINGS_CLONE_ALL_SCHEDULE}
                onClick={() => setCloneMenuOpened && setCloneMenuOpened(true)}
                className={classnames({ 'dropdown-item--disabled': !setCloneMenuOpened })}
                disabled={!setCloneMenuOpened}
              >
                <Icon kind="clone_all_agents_schedule" height={25} color="blue" />
                {t('schedule.cloneAllPeopleSchedule')}
              </DropdownItem>
              <PinUnpinSubmenus
                pinnableElementsInCurrentDayCount={pinnableGanttScheduleElementsIds.length}
                pinnedElementsCount={pinnedUsedScheduleElementsIds.length}
                pinnedElementsInCurrentDayCount={pinnedScheduleElementsIds.length}
                pinnedSelectedElementsInCurrentDayCount={pinnedSelectedScheduleElementsIds.length}
                pinnableSelectedElementsInCurrentDayCount={pinnableSelectedScheduleElementsIds.length}
                pinnableElementsCount={totalPinnableElementIds.length}
                pinAllScheduleElements={() => dispatch(pinAllScheduleElements())}
                pinElementsInCurrentDay={() => dispatch(pinElementsInCurrentDay())}
                pinSelectedElements={() => dispatch(pinSelectedElements())}
                unpinAllScheduleElements={() => dispatch(unpinAllScheduleElements())}
                unpinElementsInCurrentDay={() => dispatch(unpinElementsInCurrentDay())}
                unpinSelectedElements={() => dispatch(unpinSelectedElements())}
              />
            </>
          )}
        </>
      ),
    },
  };

  return <UncontrolledDropdown {...dropdownProps} />;
};
